/**
 * Inky's per-turn system prompt. Rebuilt every turn (Flint prompt.ts pattern): identity,
 * session, what is on screen (with entity ids), the open document's outline and selection,
 * retrieved project context, and numbered ground rules. Pure module.
 */

/** Max entities of each kind listed with ids in the prompt directory. */
const DIRECTORY_LIMIT = 15;
/** Max characters of a block's text in the outline. */
const OUTLINE_TEXT_CHARS = 160;
/** Max outline blocks listed in the prompt (the doc_edit planner sees the full document). */
const OUTLINE_LIMIT = 80;
/** Max characters of selected text quoted in the prompt. */
const SELECTION_CHARS = 1500;

export const INKY_NAME = "Inky";

/**
 * Collapse whitespace and cut a string to `max` characters.
 * @param {string} value - Raw text.
 * @param {number} max - Max characters.
 * @returns {string} One-line text.
 */
export const oneLine = (value, max = 120) => {
  const flat = String(value || "").replace(/\s+/g, " ").trim();
  return flat.length > max ? `${flat.slice(0, max - 1)}…` : flat;
};

/**
 * Directory of entities with ids, so the model never has to invent one.
 * @param {object} contextData - Customers, projects, templates.
 * @returns {string} Directory section.
 */
const buildDirectory = (contextData) => {
  const customers = contextData?.customers || [];
  const projects = contextData?.projects || [];
  const templates = contextData?.templates || [];
  const customerName = (id) => customers.find((c) => c.id === id)?.name;
  const list = (items, render) => {
    if (!items.length) return "  (none)";
    const lines = items.slice(0, DIRECTORY_LIMIT).map(render);
    if (items.length > DIRECTORY_LIMIT) {
      lines.push(`  …and ${items.length - DIRECTORY_LIMIT} more (use data_search to find others)`);
    }
    return lines.join("\n");
  };
  return [
    `Customers (${customers.length}):`,
    list(customers, (c) => `  - ${oneLine(c.name, 60)} (id: ${c.id})`),
    `Projects (${projects.length}):`,
    list(projects, (p) => {
      const owner = customerName(p.customer_id) || p.customer?.name;
      const counts = `${(p.requirements || []).length} requirements, ${(p.sessions || []).length} sessions, ${(p.generatedDocuments || []).length} documents`;
      return `  - ${oneLine(p.name, 60)} (id: ${p.id}${owner ? `, customer: ${oneLine(owner, 40)}` : ""}; ${counts})`;
    }),
    `Templates (${templates.length}):`,
    list(templates, (t) => `  - ${oneLine(t.name, 60)} (id: ${t.id}${t.templateType && t.templateType !== "document" ? `, ${t.templateType}` : ""})`),
  ].join("\n");
};

/**
 * Describe the open editor surface: kind, title, outline with block ids, selection, review.
 * @param {object|null} surface - Editor snapshot from the agent runtime.
 * @returns {string} Surface section (empty when no editor is open).
 */
const buildSurfaceSection = (surface) => {
  if (!surface) return "";
  const lines = [];
  const kind = surface.meta?.kind === "template" ? "template" : "document";
  lines.push(
    `An editor is open on the ${kind} "${oneLine(surface.meta?.title || "Untitled", 80)}"` +
      `${surface.meta?.documentId ? ` (document id: ${surface.meta.documentId})` : ""}` +
      `${surface.meta?.templateId ? ` (template id: ${surface.meta.templateId})` : ""}` +
      `${surface.meta?.readOnly ? " in read-only mode" : ""}.`
  );
  if (surface.project) {
    lines.push(`It belongs to project "${oneLine(surface.project.name, 60)}" (id: ${surface.project.id}).`);
  }
  if (surface.isEmpty) {
    lines.push("The document is currently EMPTY.");
  } else if (Array.isArray(surface.blocks) && surface.blocks.length) {
    lines.push(`Outline (${surface.blocks.length} blocks; ids are stable, cite them as targetBlockIds):`);
    for (const block of surface.blocks.slice(0, OUTLINE_LIMIT)) {
      const level = block.level ? `h${block.level}` : block.type;
      const locked = block.locked ? " [locked]" : "";
      if (block.type === "pageBreak") {
        lines.push(`  [${block.id}] page break`);
        continue;
      }
      lines.push(`  [${block.id}] ${level}${locked}: ${oneLine(block.text, OUTLINE_TEXT_CHARS) || "(empty)"}`);
    }
    if (surface.blocks.length > OUTLINE_LIMIT) {
      lines.push(`  …${surface.blocks.length - OUTLINE_LIMIT} more blocks (call doc_read for the rest).`);
    }
  } else if (surface.wordCount != null) {
    lines.push(`The document has about ${surface.wordCount} words.`);
  }
  if (surface.selection?.text) {
    lines.push(
      `The user has selected${surface.selection.blockIds?.length ? ` (blocks ${surface.selection.blockIds.join(", ")})` : ""}: "${oneLine(surface.selection.text, SELECTION_CHARS)}"`
    );
  }
  if (surface.pendingReview?.changes?.length) {
    lines.push(
      `${surface.pendingReview.changes.length} suggested change(s) from your last edit are still awaiting the user's review. A follow-up like "make it shorter" refines that edit: call doc_edit again and say it is a refinement.`
    );
  }
  return `\n\n## What is open\n${lines.join("\n")}`;
};

/**
 * Ground rules, numbered so they can be referred to.
 * @param {{legacy: boolean, hasEditor: boolean}} options - Prompt mode.
 * @returns {string} Rules section.
 */
const buildRules = ({ legacy, hasEditor }) => {
  const tool = (name) => (legacy ? name : name.replace(/\./g, "_"));
  const rules = [
    `Bias to action. When the request is clear, use your tools right away. Reading data and editing the open document never need permission (edits always land as suggestions the user reviews).`,
    `Ask a clarifying question only when the request is genuinely ambiguous AND the action is destructive (for example replacing most of a document). Ask one short question, never several. Otherwise choose a sensible default and say which default you used.`,
    `Never invent requirements, decisions, dates, owners, numbers or names. Answer only from tool results, the context below, and what the user said. If something is unknown, say so plainly, or leave "[Needs input: what is missing]" in drafted text.`,
    `Finish the turn. Never say "I will…" or "Let me…" without doing it in the same turn. Do not narrate tool calls; describe outcomes in plain words.`,
    `Use the ids from the directory and from tool results. Never guess an id; call ${tool("data.search")} when you are unsure which entity the user means.`,
    hasEditor
      ? `For any change to the open document or template call ${tool("doc.edit")} with a complete, self-contained instruction: include everything relevant the user said in earlier messages ("like we discussed" must be spelled out), the tone, length and sections wanted, and targetBlockIds when they refer to specific sections or the selection. Page breaks are blocks in the outline: to add one ("start Appendix on a new page") pass the heading it goes before as targetBlockIds and say "insert a page break before this section"; never ask doc.edit to drop page breaks unless the user asked. Nothing is saved until the user accepts the suggestions in the editor; say so in one short sentence after an edit.`
      : `No editor is open, so you cannot edit a document directly. To create a document from a template use ${tool("doc.generate")}; to work on an existing one, link it so the user can open it.`,
    `Creating customers or projects, staging requirement changes and generating documents are confirmed by the user on a card in this chat. When you call those tools, tell the user what you prepared and that it waits for their confirmation (name the button: Confirm, Approve all or Generate); never claim it already happened.`,
    `Requirement titles, ids and quoted meeting text stay verbatim. Ground drafts in the project's requirements and meeting notes and cite them as written.`,
    `Navigate only when the user asks to open or go somewhere.`,
    `Write in concise markdown: short paragraphs, bullet lists when listing, bold sparingly. Link entities with app paths: projects /projects/<id>, documents /documents/<id>/edit, sessions /customers/sessions/<id>, customers /customers/<id>, templates /templates/<id>/edit. Never show raw JSON, ids without a name, or tool names.`,
  ];
  return `\n\n## Ground rules\n${rules.map((rule, index) => `${index + 1}. ${rule}`).join("\n")}`;
};

/**
 * Build Inky's system instruction for one turn.
 * @param {object} params - Prompt inputs.
 * @param {string} [params.contextSummary] - Route/user/account summary from the app shell.
 * @param {object} [params.contextData] - Entity arrays for the directory.
 * @param {object|null} [params.surface] - Open editor snapshot.
 * @param {string} [params.retrievedContext] - Retrieved project file snippets.
 * @param {boolean} [params.legacy] - True for the legacy text-JSON protocol.
 * @param {string} [params.legacyToolsJson] - Compact tool specs for the legacy protocol.
 * @param {Date} [params.now] - Clock (tests).
 * @returns {string} System instruction.
 */
export const buildInkySystemPrompt = ({
  contextSummary = "",
  contextData = null,
  surface = null,
  retrievedContext = "",
  legacy = false,
  legacyToolsJson = "",
  now = new Date(),
}) => {
  const identity =
    `You are ${INKY_NAME}, the assistant inside Quill, a workspace where product and project managers record meetings, extract requirements and generate documents from reusable templates. ` +
    `You are a friendly octopus with a lot of arms and very little patience for fluff: concise, warm, practical and PM-savvy. ` +
    `You help users edit the open document or template, draft documents from a project's requirements and meeting notes, answer questions about their customers, projects, sessions and requirements, and get around the app.`;

  const session = `\n\n## Session\nToday is ${now.toISOString().slice(0, 10)}.\n${String(contextSummary || "").trim()}`;
  const directory = contextData ? `\n\n## Directory (names with ids)\n${buildDirectory(contextData)}` : "";
  const retrieval = retrievedContext
    ? `\n\n## Relevant project files (retrieved for this message; cite by file name)\n${retrievedContext}`
    : "";

  const legacyProtocol = legacy
    ? `\n\n## Tools\n${legacyToolsJson}\n\n## Reply format\nReply with exactly one JSON object and nothing else (no code fences, no text around it):\n{"type":"message","content":"<markdown reply to the user>"}\nor\n{"type":"tool_call","toolName":"<tool name>","args":{...}}\nTool results come back as "[Tool result for <name>]" messages; read them and continue. Put only the user-facing reply in "content".`
    : "";

  return [
    identity,
    session,
    directory,
    buildSurfaceSection(surface),
    retrieval,
    buildRules({ legacy, hasEditor: Boolean(surface) }),
    legacyProtocol,
  ].join("");
};
